import {
  apiToAssetDownloadV2Props,
  AssetDownload,
} from "~/components/AssetDownload";
import { ContentItemWithImageProps } from "~/components/ContentItemWithImage";
import { Divider } from "~/components/Divider";
import { apiToGenericSearchProps } from "~/components/GenericSearch/apiToGenericSeachProps";
import { HubSpotForm } from "~/components/HubSpotForm";
import { LandingPageTabbedNavigation } from "~/components/LandingPageTabbedNavigation/LandingPageTabbedNavigation";
import { LandingPageTabbedNavigationTab } from "~/components/LandingPageTabbedNavigation/LandingPageTabbedNavigationTab";
import { FeatureListColumnGridBlock } from "~/components/LandingPageV2/components/FeatureListColumnGridBlock";
import { LandingPageContainer } from "~/components/LandingPageV2/components/LandingPageContainer";
import { LandingPageContent } from "~/components/LandingPageV2/components/LandingPageContent";
import { LandingPageSearch } from "~/components/LandingPageV2/components/LandingPageSearch";
import { LeadGenerationFormBlock } from "~/components/LandingPageV2/components/LeadGenerationFormBlock";
import { SplitWithImageContainer } from "~/components/LandingPageV2/components/SplitWithImageContainer";
import { WysiwygBlock } from "~/components/LandingPageV2/components/WysiwygBlock";
import { LandingPageBuildHtml } from "~/components/LandingPageV2/LandingPageBuildHtml";
import { ReportLibraryEntryFeatured } from "~/components/ReportLibraryEntryFeatured";
import {
  ReportLibraryEntryRecentlyPublished,
  ReportLibraryRecentlyPublishedPost,
} from "~/components/ReportLIbraryEntryRecentlyPublished";
import {
  LatestReport,
  ReportLibraryHorizontalLatest,
} from "~/components/ReportLibraryHorizontalLatest";
import { ReportLibraryFeatured } from "~/components/ReportLibraryHorizontalPage/ReportLibraryFeatured";
import { ReportLibraryHorizontalTabbedContainer } from "~/components/ReportLibraryHorizontalTabbedContainer";
import { YouTubeEmbedBlock } from "~/components/YouTubeEmbedBlock/YouTubeEmbedBlock";
import { commonConfig } from "~/config/common-config";
import { InvalidProperties } from "~/landing-pages/components/Errors/InvalidProperties/InvalidProperties";
import { UnknownBlockType } from "~/landing-pages/components/Errors/UnknownBlockType/UnknownBlockType";
import {
  getDateDayTwoDigit,
  getDateMonthShortName,
} from "~/lib/shared/dateFormatters";
import {
  getRouteFromPostType,
  PostToRoutePostTypes,
} from "~/lib/shared/getRouteFromPostType";
import { getValueOrDefault } from "~/lib/shared/getValueOrDefault";
import { heDecodeOrDefault } from "~/lib/shared/html-utils";
import { sanitizeRelativeUrl } from "~/lib/shared/sanitizeRelativeUrl";
import { FileUploadACFV2Payload } from "~/types/asgard/FileUpload";
import { PostSummary } from "~/types/asgard/PostSummary";
import { FullWidthImage } from "~/ui-library";
import {
  isAnchorBlock,
  isContentListCuratedBlock,
  isContentListSplitBlock,
  isContentListTabbedBlock,
  isDownloadableAssetsBlock,
  isFeatureListBlock,
  isHubSpotFormEmbedBlock,
  isImageBlock,
  isInvalidPropertiesError,
  isLeadGenerationBlock,
  isSearchBlock,
  isTabbedNavigationBlock,
  isUnknownBlockType,
  isWysiwygBlock,
  isYoutubeEmbedBlock,
} from "./landing-page-block-type-guards";
import { LandingPageBlockV2 } from "./schemas";

const featureListAppearanceToColumns = {
  two_column_grid: 2,
  three_column_grid: 3,
  four_column_grid: 4,
} as const;

const contentSummaryToRecentlyPublishedPost = ({
  slug,
  title,
  type,
  primaryCategory,
  dateGmt,
}: PostSummary): ReportLibraryRecentlyPublishedPost => ({
  headline: heDecodeOrDefault(title),
  headlineHref: getRouteFromPostType(type, slug),
  id: slug,
  date: dateGmt && new Date(dateGmt).toISOString(),
  kicker: heDecodeOrDefault(primaryCategory?.name),
  kickerHref: undefined,
});

const postSummaryToLatestPost = ({
  slug,
  title,
  type,
  dateGmt,
  image,
  primaryCategory,
}: PostSummary): LatestReport => ({
  category: heDecodeOrDefault(primaryCategory?.name),
  day: getDateDayTwoDigit(dateGmt),
  month: getDateMonthShortName(dateGmt),
  href: getRouteFromPostType(type as PostToRoutePostTypes, slug),
  imageSrc: getValueOrDefault(image?.source, ""),
  isChartPack: false,
  title: heDecodeOrDefault(title),
});

const postSummaryToContentItem = ({
  slug,
  title,
  type,
  dateGmt,
  image,
  primaryCategory,
}: PostSummary): ContentItemWithImageProps => ({
  imageSrc: getValueOrDefault(image?.source, ""),
  headline: title,
  date: dateGmt,
  displayDate: true,
  displayKicker: true,
  kicker: primaryCategory?.name,
  headlineHref: getRouteFromPostType(type, slug),
});

export const LandingPageBlocks = ({
  postId,
  postName,
  blocks,
  preview = false,
  showBlockOverlay,
}: {
  postId: number;
  postName: string;
  blocks: LandingPageBlockV2[];
  preview?: boolean;
  showBlockOverlay?: boolean;
}): JSX.Element | null => {
  if (!blocks?.length) {
    return null;
  }
  const contentBlockComponents = blocks.map((block, i) => {
    const debugBlockInfo = { blockIndex: i + 1, blockType: block.acfFcLayout };

    /** Anchor */
    if (isAnchorBlock(block)) {
      return <div key={`${block.acfFcLayout}-${i}`} id={block.anchorId}></div>;
    }

    if (isContentListCuratedBlock(block)) {
      const posts = block.posts.map<ContentItemWithImageProps>(
        ({ image, title, slug, type, dateGmt, primaryCategory }) => ({
          imageSrc: getValueOrDefault(image?.source, ""),
          imageAlt: getValueOrDefault(image?.altText, ""),
          headline: title,
          displayFooter: true,
          headlineHref: getRouteFromPostType(
            type as PostToRoutePostTypes,
            slug
          ),
          date: dateGmt,
          kicker: primaryCategory?.name,
        })
      );
      if (posts.length === 1) {
        return (
          <LandingPageContainer
            background={block.background}
            key={`${block.acfFcLayout}-${i}`}
            topPadding={block.topSpacing}
            bottomPadding={block.bottomSpacing}
            shouldShowBlockSkeleton={showBlockOverlay}
            blockInfo={debugBlockInfo}
          >
            <LandingPageContent maxWidth={block.maxWidth}>
              {!!block.wysiwyg && (
                <div className="mb-m">
                  <LandingPageBuildHtml html={block.wysiwyg} />
                </div>
              )}
              <ReportLibraryEntryFeatured posts={posts} title="" />
            </LandingPageContent>
          </LandingPageContainer>
        );
      }

      return (
        <LandingPageContainer
          background={block.background}
          key={`${block.acfFcLayout}-${i}`}
          topPadding={block.topSpacing}
          bottomPadding={block.bottomSpacing}
          shouldShowBlockSkeleton={showBlockOverlay}
          blockInfo={debugBlockInfo}
        >
          <LandingPageContent maxWidth={block.maxWidth}>
            {!!block.wysiwyg && (
              <div className="mb-m">
                <LandingPageBuildHtml html={block.wysiwyg} />
              </div>
            )}
            <ReportLibraryEntryFeatured posts={posts} title="" />
          </LandingPageContent>
        </LandingPageContainer>
      );
    }

    if (isContentListSplitBlock(block)) {
      return (
        <LandingPageContainer
          background={block.background}
          key={`${block.acfFcLayout}-${i}`}
          topPadding={block.topSpacing}
          bottomPadding={block.bottomSpacing}
          shouldShowBlockSkeleton={showBlockOverlay}
          blockInfo={debugBlockInfo}
        >
          <LandingPageContent maxWidth={block.maxWidth}>
            {!!block.wysiwyg && (
              <div className="mb-m">
                <LandingPageBuildHtml html={block.wysiwyg} />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="border-gray-300 py-l md:border-r-1 md:border-t-1 md:pb-xl md:pr-l md:pt-m">
                <ReportLibraryFeatured
                  featuredPost={postSummaryToContentItem(
                    block.featured.featuredPost
                  )}
                  featuredDescription={block.featured.featuredPost.subhead}
                  title={block.featured.sectionTitle}
                />
                <Divider className="mt-l md:hidden" />
              </div>

              <div>
                <div className="pt-l md:border-t-1 md:border-gray-300 md:py-m md:pl-l">
                  <ReportLibraryHorizontalTabbedContainer
                    tabs={[
                      {
                        label: getValueOrDefault(
                          block.latest.sectionTitle,
                          "Latest"
                        ),
                        component: (
                          <ReportLibraryHorizontalLatest
                            latestReports={block.latest.posts
                              .slice(0, 5)
                              .map(postSummaryToLatestPost)}
                          />
                        ),
                      },
                    ]}
                  />
                </div>
              </div>
            </div>
          </LandingPageContent>
        </LandingPageContainer>
      );
    }

    if (isContentListTabbedBlock(block)) {
      return (
        <LandingPageContainer
          background={block.background}
          key={`${block.acfFcLayout}-${i}`}
          topPadding={block.topSpacing}
          bottomPadding={block.bottomSpacing}
          shouldShowBlockSkeleton={showBlockOverlay}
          blockInfo={debugBlockInfo}
        >
          <LandingPageContent maxWidth={block.maxWidth}>
            {!!block.wysiwyg && (
              <div className="mb-m">
                <LandingPageBuildHtml html={block.wysiwyg} />
              </div>
            )}
            <ReportLibraryEntryRecentlyPublished
              title=""
              tabs={block.tabs.map(({ label, posts }) => ({
                posts: posts.map(contentSummaryToRecentlyPublishedPost),
                slug: "",
                title: label,
              }))}
            />
          </LandingPageContent>
        </LandingPageContainer>
      );
    }

    if (isDownloadableAssetsBlock(block)) {
      const assetDownloadProps = apiToAssetDownloadV2Props({
        postId,
        assetSummary: "",
        downloadableAssets: block.downloadableAssets as FileUploadACFV2Payload,
        postName,
      });

      return (
        <LandingPageContainer
          background={block.background}
          key={`${block.acfFcLayout}-${i}`}
          topPadding={block.topSpacing}
          bottomPadding={block.bottomSpacing}
          shouldShowBlockSkeleton={showBlockOverlay}
          blockInfo={debugBlockInfo}
        >
          <LandingPageContent maxWidth={block.maxWidth}>
            {!!block.wysiwyg && (
              <div className="mb-m">
                <LandingPageBuildHtml html={block.wysiwyg} />
              </div>
            )}
            <AssetDownload
              postId={getValueOrDefault(assetDownloadProps?.postId, 0)}
              assets={getValueOrDefault(assetDownloadProps?.assets, [])}
              postName={postName}
              showHeader={false}
            />
          </LandingPageContent>
        </LandingPageContainer>
      );
    }

    if (isFeatureListBlock(block)) {
      if (
        ["two_column_grid", "three_column_grid", "four_column_grid"].includes(
          block.appearance
        )
      ) {
        return (
          <LandingPageContainer
            background={block.background}
            key={`${block.acfFcLayout}-${i}`}
            topPadding={block.topSpacing}
            bottomPadding={block.bottomSpacing}
            shouldShowBlockSkeleton={showBlockOverlay}
            blockInfo={debugBlockInfo}
          >
            <LandingPageContent maxWidth={block.maxWidth}>
              {block.wysiwyg ? (
                <div className="mb-xl">
                  <LandingPageBuildHtml html={block.wysiwyg} />
                </div>
              ) : null}
              <FeatureListColumnGridBlock
                columns={featureListAppearanceToColumns[block.appearance]}
                features={block.features}
              />
            </LandingPageContent>
          </LandingPageContainer>
        );
      }
    }

    if (isImageBlock(block)) {
      return (
        <LandingPageContainer
          background={block.background}
          blockInfo={debugBlockInfo}
          bottomPadding={block.bottomSpacing}
          key={`${block.acfFcLayout}-${i}`}
          shouldShowBlockSkeleton={showBlockOverlay}
          topPadding={block.topSpacing}
        >
          <LandingPageContent maxWidth={block.maxWidth}>
            {!!block.wysiwyg && (
              <div className="mb-xl">
                <LandingPageBuildHtml html={block.wysiwyg} />
              </div>
            )}
            <FullWidthImage
              alt={block.image.alt}
              aspectRatio="4/3"
              captionText={block.caption}
              src={block.image.url}
            />
          </LandingPageContent>
        </LandingPageContainer>
      );
    }

    /** Lead Generation Form */
    if (isLeadGenerationBlock(block)) {
      return (
        <LandingPageContainer
          background={block.background}
          key={`${block.acfFcLayout}-${i}`}
          topPadding={block.topSpacing}
          bottomPadding={block.bottomSpacing}
          shouldShowBlockSkeleton={showBlockOverlay}
          blockInfo={debugBlockInfo}
        >
          <LandingPageContent maxWidth={block.maxWidth}>
            {block.wysiwyg ? (
              <div className="mb-xl">
                <LandingPageBuildHtml html={block.wysiwyg} />
              </div>
            ) : null}
            <LeadGenerationFormBlock
              key={`${block.acfFcLayout}-${i}`}
              enableDarkMode={block.enableDarkMode}
              {...block}
            />
          </LandingPageContent>
        </LandingPageContainer>
      );
    }

    /**
     * Search
     */
    if (isSearchBlock(block)) {
      return (
        <LandingPageContainer
          background={block.background}
          key={`${block.acfFcLayout}-${i}`}
          topPadding={block.topSpacing}
          bottomPadding={block.bottomSpacing}
          shouldShowBlockSkeleton={showBlockOverlay}
          blockInfo={debugBlockInfo}
        >
          <LandingPageContent maxWidth={block.maxWidth}>
            {!!block.wysiwyg && (
              <div className="mb-l">
                <LandingPageBuildHtml html={block.wysiwyg} />
              </div>
            )}
            <LandingPageSearch {...apiToGenericSearchProps(block)} />
          </LandingPageContent>
        </LandingPageContainer>
      );
    }

    if (isTabbedNavigationBlock(block)) {
      return (
        <LandingPageContainer
          background={block.background}
          key={`${block.acfFcLayout}-${i}`}
          topPadding={block.topSpacing}
          bottomPadding={block.bottomSpacing}
          shouldShowBlockSkeleton={showBlockOverlay}
          blockInfo={debugBlockInfo}
        >
          <LandingPageContent
            maxWidth={block.maxWidth}
            className="relative -translate-y-m md:translate-y-[-50%]"
          >
            {!!block.wysiwyg && (
              <div className="mb-l">
                <LandingPageBuildHtml html={block.wysiwyg} />
              </div>
            )}
            <LandingPageTabbedNavigation title={block.label}>
              {block.links.map(({ link }, linkIndex) => (
                <LandingPageTabbedNavigationTab
                  href={sanitizeRelativeUrl(link.url)}
                  label={link.title}
                  key={linkIndex}
                />
              ))}
            </LandingPageTabbedNavigation>
          </LandingPageContent>
        </LandingPageContainer>
      );
    }

    /**
     * WYSIWYG
     */
    if (isWysiwygBlock(block)) {
      if (block.appearance === "full_width") {
        return (
          <LandingPageContainer
            background={block.background}
            key={`${block.acfFcLayout}-${i}`}
            topPadding={block.topSpacing}
            bottomPadding={block.bottomSpacing}
            shouldShowBlockSkeleton={showBlockOverlay}
            blockInfo={debugBlockInfo}
          >
            <LandingPageContent maxWidth={block.maxWidth}>
              <WysiwygBlock
                key={`${block.acfFcLayout}-${i}`}
                {...block}
                blockIndex={i}
              />
            </LandingPageContent>
          </LandingPageContainer>
        );
      }

      if (block.appearance === "split_with_image") {
        return (
          <LandingPageContainer
            background={block.background}
            key={`${block.acfFcLayout}-${i}`}
            topPadding={block.topSpacing}
            bottomPadding={block.bottomSpacing}
            shouldShowBlockSkeleton={showBlockOverlay}
            blockInfo={debugBlockInfo}
          >
            <LandingPageContent maxWidth={block.maxWidth}>
              <SplitWithImageContainer
                imageAlt={block.image.alt}
                imageUrl={block.image.url}
                imagePosition="left"
                mdImagePosition={block.imagePosition}
                verticalTextAlignment={block.verticalTextAlignment}
              >
                <WysiwygBlock
                  key={`${block.acfFcLayout}-${i}`}
                  {...block}
                  blockIndex={i}
                />
              </SplitWithImageContainer>
            </LandingPageContent>
          </LandingPageContainer>
        );
      }
    }

    if (isYoutubeEmbedBlock(block)) {
      return (
        <LandingPageContainer
          background={block.background}
          key={`${block.acfFcLayout}-${i}`}
          shouldShowBlockSkeleton={showBlockOverlay}
          topPadding={block.topSpacing}
          bottomPadding={block.bottomSpacing}
          blockInfo={debugBlockInfo}
        >
          <LandingPageContent maxWidth={block.maxWidth}>
            {block.wysiwyg ? (
              <div className="mb-xl">
                <LandingPageBuildHtml html={block.wysiwyg} />
              </div>
            ) : null}
            <YouTubeEmbedBlock src={block.videoUrl} />
          </LandingPageContent>
        </LandingPageContainer>
      );
    }

    if (isHubSpotFormEmbedBlock(block)) {
      return (
        <LandingPageContainer
          background={block.background}
          key={`${block.acfFcLayout}-${i}`}
          shouldShowBlockSkeleton={showBlockOverlay}
          topPadding={block.topSpacing}
          bottomPadding={block.bottomSpacing}
          blockInfo={debugBlockInfo}
        >
          <LandingPageContent maxWidth={block.maxWidth}>
            {block.wysiwyg ? (
              <div className="mb-xl">
                <LandingPageBuildHtml html={block.wysiwyg} />
              </div>
            ) : null}
            <HubSpotForm
              portalId={commonConfig.NEXT_PUBLIC_HUBSPOT_PORTAL_ID}
              formId={block.formId}
              formContainerId={`hs-form-${i}`}
            />
          </LandingPageContent>
        </LandingPageContainer>
      );
    }

    /**
     * Error states
     *
     * Only shown in preview mode, otherwise do not render.
     */
    if (preview) {
      if (isUnknownBlockType(block)) {
        return (
          <UnknownBlockType key={`${block.acfFcLayout}-${i}`} {...block} />
        );
      }
      if (isInvalidPropertiesError(block)) {
        return (
          <InvalidProperties key={`${block.acfFcLayout}-${i}`} {...block} />
        );
      }
    }

    return null;
  });

  return <>{contentBlockComponents}</>;
};
